import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ArrowLeft, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import BackToTop from './BackToTop';

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

// Build a shared scan function so both desktop and mobile TOCs operate
// on the same list of items.
const scanAndDecorate = (root) => {
  if (!root) return [];
  const headings = Array.from(root.querySelectorAll('h2, h3')).filter(
    (h) => h.getAttribute('data-toc-skip') !== 'true'
  );
  const seen = new Set();
  return headings.map((h) => {
    let base = h.id || slugify(h.textContent);
    let candidate = base;
    let i = 1;
    while (seen.has(candidate)) candidate = `${base}-${i++}`;
    seen.add(candidate);
    h.id = candidate;
    h.style.scrollMarginTop = '96px';
    return { id: candidate, text: h.textContent.replace(/\s+$/, '').trim(), level: h.tagName === 'H3' ? 3 : 2 };
  });
};

const TocList = ({ items, activeId, onItemClick, idPrefix = '' }) => (
  <ul className="space-y-1 border-l border-gray-200">
    {items.map((it) => {
      const active = it.id === activeId;
      const indent = it.level === 3 ? 'pl-7' : 'pl-4';
      const size = it.level === 3 ? 'text-[13px]' : 'text-sm';
      const label = it.text.replace(/\s+$/, '');
      return (
        <li key={it.id}>
          <a
            href={`#${it.id}`}
            onClick={(e) => onItemClick(e, it.id)}
            className={`block ${size} leading-snug ${indent} py-1.5 -ml-px border-l-2 transition-colors ${
              active
                ? 'border-[#ef5b25] text-[#0a1929] font-semibold'
                : 'border-transparent text-gray-500 hover:text-[#0a1929]'
            }`}
            data-testid={`${idPrefix}toc-link-${it.id}`}
          >
            {label}
          </a>
        </li>
      );
    })}
  </ul>
);

const LegalPageLayout = ({
  eyebrow = 'Legal',
  title,
  lead,
  lastUpdated,
  topRightSlot,
  children,
  testId,
  tocRefreshKey,
}) => {
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  // Scan headings + inject copy-link buttons whenever content changes
  useEffect(() => {
    const next = scanAndDecorate(contentRef.current);
    setItems(next);
    if (next.length > 0) setActiveId((prev) => (next.find((n) => n.id === prev) ? prev : next[0].id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tocRefreshKey, title]);

  // Track active section
  useEffect(() => {
    if (items.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -65% 0px', threshold: [0, 1] }
    );
    items.forEach((it) => {
      const el = document.getElementById(it.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [items]);

  const handleClick = useCallback((e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
    setMobileTocOpen(false);
    window.history.replaceState(null, '', `#${id}`);
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid={testId}>
      <Header />
      <div className="h-20" />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0a1929] via-[#0f2944] to-[#1F4E79] text-white">
        <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ef5b25]/15 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-white/5 blur-3xl" aria-hidden="true" />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 sm:pt-12 sm:pb-16">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/80 hover:text-white font-medium mb-8 transition-colors duration-200"
            data-testid="legal-back-button"
          >
            <ArrowLeft size={18} />
            Back
          </button>
          <p className="text-[11px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">
            {eyebrow}
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
                {title}
              </h1>
              {lead && (
                <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed">
                  {lead}
                </p>
              )}
            </div>
            {topRightSlot && <div className="flex-shrink-0">{topRightSlot}</div>}
          </div>
          {lastUpdated && (
            <p className="mt-6 text-sm text-white/60">Last updated: {lastUpdated}</p>
          )}
        </div>
      </section>

      {/* Body */}
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
          {/* Mobile / tablet collapsible TOC — shown below lg */}
          {items.length > 0 && (
            <div className="lg:hidden mb-8 border border-gray-200 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setMobileTocOpen((o) => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                aria-expanded={mobileTocOpen}
                data-testid="mobile-toc-toggle"
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                  <ListOrdered size={16} className="text-[#ef5b25]" />
                  On this page
                  <span className="text-gray-400 font-normal">({items.length})</span>
                </span>
                <ChevronDown
                  size={18}
                  className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileTocOpen && (
                <nav className="px-4 py-3 bg-white max-h-[60vh] overflow-y-auto" data-testid="mobile-toc-panel">
                  <TocList items={items} activeId={activeId} onItemClick={handleClick} idPrefix="m-" />
                </nav>
              )}
            </div>
          )}

          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_220px] lg:gap-12">
            <div
              ref={contentRef}
              className="legal-prose text-[15px] sm:text-base text-gray-700 leading-relaxed min-w-0"
            >
              {children}
            </div>
            {items.length > 0 && (
              <aside
                className="hidden lg:block lg:sticky lg:top-24 self-start max-h-[calc(100vh-6rem)] overflow-y-auto pr-1"
                aria-label="Table of contents"
                data-testid="on-this-page"
              >
                <p className="text-[10px] font-bold text-[#ef5b25] uppercase tracking-[0.22em] mb-3">
                  On this page
                </p>
                <nav>
                  <TocList items={items} activeId={activeId} onItemClick={handleClick} />
                </nav>
              </aside>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
};

export default LegalPageLayout;
